<template>
    <div>
        <SectionHeader
            v-if="hasAutomaticAccounts"
            title="La conciliación bancaria te ayuda a mantener tus obligaciones al día"
        >
            <div class="is-flex is-align-items-center">
                <p class="is-size-6 mr-2">
                    Aquí encontrarás todos los movimientos informados por tus
                    bancos de forma automática y se actualizan cada 60 minutos.
                </p>
            </div>
        </SectionHeader>
        <Notification
            v-else
            @on-connect="SET_IS_ACT_FINANCE_ACC(true)"
            class="mb-5"
        />
        <AccountsCards
            v-if="hasAutomaticAccounts"
            class="mb-6"
            :accounts="financialEntityAccList"
        />
        <ChartFilterLayout v-if="hasAutomaticAccounts" role="chartFilterLayout">
            <FilterBankMovements
                role="filterBankMovements"
                @on-filter="onFilter"
                @on-clean-filter="onCleanFilter"
                :autocomplete-data="autocompleteData"
            />
            <ChartBankMovements
                role="chartBankMovements"
                :start-date="startDate"
                :end-date="endDate"
                :chart-data="cashFlowData"
            />
            <ListBankMovements
                role="tableBankMovements"
                :total="numberRegisters"
                :data="bankMovements"
                @on-page-change="onPageChange"
                :current-page="tableDataParams.page"
                :loading="loadingMovements"
            />
        </ChartFilterLayout>
    </div>
</template>

<script>
import { filterPeriodOptions } from "@/components/chartFilter/componentData";
import ChartFilterLayout from "@/components/chartFilterLayout/ChartFilterLayout";
import SectionHeader from "@/components/sectionHeader/SectionHeader";
import { consoleHandlerError } from "@/utils/message";
import moment from "moment";
import { mapActions, mapMutations, mapState } from "vuex";
import AccountsCards from "./AccountsCards";
import ChartBankMovements from "./ChartBankMovements";
import FilterBankMovements from "./FilterBankMovements";
import ListBankMovements from "./ListBankMovements";
import Notification from "./Notification";

const initialStartDate = filterPeriodOptions.find(
    (item) => item.id === "last12Months"
).start_date;
const initialEndDate = filterPeriodOptions.find(
    (item) => item.id === "last12Months"
).end_date;

export default {
    name: "bankMovements",
    components: {
        SectionHeader,
        FilterBankMovements,
        ListBankMovements,
        AccountsCards,
        ChartBankMovements,
        ChartFilterLayout,
        Notification,
    },
    data() {
        return {
            movementTypes: ["C", "A"],
            startDate: initialStartDate,
            endDate: initialEndDate,
            chartData: [],
            banks: [],
            page: 1,
        };
    },
    methods: {
        ...mapMutations("financialEntity", [
            "SET_IS_ACT_FINANCE_ACC",
            "SET_TABLE_DATA_PARAMS",
            "SET_DETAILS_OPEN_LIST",
        ]),
        ...mapActions("financialEntity", ["getBankMovements"]),
        ...mapActions("cashFlowHistory", ["getCashFlowHistory"]),
        updateAll() {
            this.SET_DETAILS_OPEN_LIST([]);
            const hasBanks = this.banks.length > 0;
            const strBanks = this.banks.toString();
            const endDate = moment(this.endDate)
                .endOf("day")
                .format("YYYY-MM-DD HH:mm:ss");

            const params = {
                ...(hasBanks && {
                    owner_account__financial_entity__id__in: strBanks,
                }),
                ...(!this.movementTypes.includes("C") &&
                    this.movementTypes.includes("A") && {
                        amount__gt: 0,
                    }),
                ...(this.movementTypes.includes("C") &&
                    !this.movementTypes.includes("A") && {
                        amount__lt: 0,
                    }),
                ...(this.description && {
                    search: this.description,
                }),
                accountant_date__gte: this.startDate,
                accountant_date__lte: endDate,
                page: this.page,
                ordering: "-accountant_date",
            };
            this.SET_TABLE_DATA_PARAMS(params);
            this.getBankMovements().catch((error) => {
                consoleHandlerError({ error, empty: true });
            });
        },
        // filter only should work when selecting 1 from the checkbox options
        // abono or cargo
        isUsingFilterMovementTypes() {
            if (this.movementTypes.length === 1) {
                return true;
            }
            return false;
        },
        updateChart() {
            const chartParams = {
                start_date: this.startDate,
                end_date: this.endDate,
                ...(this.banks.length && { banks: this.banks.toString() }),
                ...(this.isUsingFilterMovementTypes() && {
                    movement_types: this.movementTypes.toString(),
                }),
                ...(this.description && {
                    search: this.description,
                }),
            };
            this.getCashFlowHistory(chartParams).catch((error) => {
                consoleHandlerError({ error, empty: true });
            });
        },
        onPageChange(page) {
            this.page = page;
            this.updateAll();
        },
        onFilter(data) {
            this.startDate = data.start_date;
            this.endDate = data.end_date;
            this.banks = data.banks;
            this.movementTypes = data.movementTypes;
            this.description = data.description;
            this.page = 1;
            this.updateAll();
            this.updateChart();
        },
        onCleanFilter() {
            this.startDate = initialStartDate;
            this.endDate = initialEndDate;
            this.banks = [];
            this.movementTypes = ["C", "A"];
            this.description = "";
            this.page = 1;
            this.updateAll();
            this.updateChart();
        },
    },
    computed: {
        ...mapState("financialEntity", [
            "bankMovements",
            "hasAutomaticAccounts",
            "tableDataParams",
            "financialEntityAccList",
            "numberRegisters",
            "loadingMovements",
        ]),
        ...mapState("cashFlowHistory", ["cashFlowData"]),
        autocompleteData() {
            let financialEntityAccList = this.financialEntityAccList.reduce(
                (prev, current) => {
                    const itemInList = prev.find(
                        (item) =>
                            item.financial_entity.name ===
                            current.financial_entity.name
                    );
                    if (itemInList) {
                        return prev;
                    }
                    return [...prev, current];
                },
                []
            );

            return financialEntityAccList.map((item) => {
                return {
                    name: item.financial_entity.name,
                    id: item.financial_entity.id,
                };
            });
        },
    },
    mounted() {
        this.updateAll();
        this.updateChart();
    },
};
</script>
